import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '../../services/api';

const Card = styled.div`
  max-width: 720px;
  margin: 2rem auto;
  padding: 2rem;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const Eyebrow = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const TenantBox = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 1rem;
  margin: 1.5rem 0;
`;

const ScopeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 1.75rem;
`;

const Scope = styled.li`
  display: flex;
  gap: 0.75rem;
  padding: 0.8rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const Button = styled.button<{ $secondary?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  font-weight: 600;
  background: ${({ theme, $secondary }) => $secondary ? 'transparent' : theme.colors.primary};
  color: ${({ theme, $secondary }) => $secondary ? theme.colors.primary : theme.colors.text.light};

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorBox = styled.div`
  color: #721c24;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  padding: 1rem;
`;

interface AuthorizationData {
  client: { id: string; name: string; uri?: string; software?: string; version?: string };
  tenant: { id: string; name: string; domain: string };
  scopes: Array<{ id: string; label: string }>;
  expires_at: string;
}

const AdminMcpAuthorization: React.FC = () => {
  const [params] = useSearchParams();
  const requestSecret = params.get('request');
  const [data, setData] = useState<AuthorizationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!requestSecret) {
      setError('Solicitação MCP ausente. Inicie novamente pelo Codex.');
      return;
    }
    api.get(`/mcp/oauth/requests/${encodeURIComponent(requestSecret)}`)
      .then(response => setData(response.data))
      .catch(err => setError(err.response?.data?.detail || 'Não foi possível carregar a autorização MCP.'));
  }, [requestSecret]);

  const decide = async (approved: boolean) => {
    if (!requestSecret) return;
    try {
      setSubmitting(true);
      setError(null);
      const response = await api.post(
        `/mcp/oauth/requests/${encodeURIComponent(requestSecret)}/decision`,
        { approved },
      );
      window.location.assign(response.data.redirect_to);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Não foi possível concluir a autorização.');
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Eyebrow>Conexão segura</Eyebrow>
      <Title>Autorizar acesso do MCP</Title>
      {error && <ErrorBox>{error}</ErrorBox>}
      {!data && !error && <Description>Carregando solicitação…</Description>}
      {data && (
        <>
          <Description>
            <strong>{data.client.name}</strong> quer acessar a imobiliária abaixo em seu nome.
            Revise as permissões antes de continuar.
          </Description>
          <TenantBox>
            <strong>{data.tenant.name}</strong><br />
            <span>{data.tenant.domain}</span>
          </TenantBox>
          <strong>Permissões solicitadas</strong>
          <ScopeList>
            {data.scopes.map(scope => <Scope key={scope.id}>{scope.label}</Scope>)}
          </ScopeList>
          <Description>
            O MCP nunca receberá sua senha. A conexão terá tokens próprios, poderá ser revogada
            nas configurações e continuará exigindo confirmação separada para escritas.
          </Description>
          <Actions>
            <Button $secondary disabled={submitting} onClick={() => decide(false)}>Recusar</Button>
            <Button disabled={submitting} onClick={() => decide(true)}>
              {submitting ? 'Autorizando…' : 'Autorizar MCP'}
            </Button>
          </Actions>
        </>
      )}
    </Card>
  );
};

export default AdminMcpAuthorization;
